/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect } from "react";
import { nanoid } from "nanoid";
import { Button } from "@showitinc/showit-wc";

import useStoreState from "../../../components/providers/useStoreState";
import AppConstants from "../../../constants/AppConstants";
import SiteInfo from "./SiteInformation";
import SocialLinks from "./SocialLinks";
import BlogSettings from "./BlogSettings";
import Integrations from "./Integrations";

import SiteSettingsStore, {
  TAB_BLOG,
  TAB_INTEGRATIONS,
  TAB_SOCIAL,
  TAB_LABEL_MAP,
  TAB_VIEW_LABEL_MAP,
  VIEW_TABS,
  VIEW_FACEBOOK_APP_ID,
  TAB_UPGRADE,
} from "../../../stores/SiteSettingsStore";
import useRenderer from "../../../hooks/query/useRenderer";
import { useSiteState } from "../../../hooks/query/useSite";
import SiteSettingsActions from "../../../actions/SiteSettingsActions";
import { Modal, ContentPanel } from "../../base/Modal";
import withAccountState from "../../../components/providers/withAccountState";
import SiteDesignStore from "../../../stores/SiteDesignStore";
import DesignViewStore from "../../../stores/DesignViewStore";
import SiteInformationProperties from "./SiteInformationProperties";
import UIActions from "../../../actions/UIActions";
import UserActions from "../../../actions/UserActions";
import connectToStores from "../../../components/providers/connectToStores";
import HelpTooltip from "../../../components/HelpTooltip";
import ExportActions from "../../../actions/ExportActions";
import DocumentStore from "../../../document/DocumentStore";
import { COMMANDS } from "../../../document/commands";
import styles from "./SiteSettingsModal.module.css";
import UpgradePanel from "./UpgradePanel";

const generateCustomCode = (customCodeCount, canvasSetIds = []) => ({
  id: nanoid(),
  label: `Custom Code ${customCodeCount + 1}`,
  applyTo: canvasSetIds,
});

const { MODAL_SITE_SETTINGS, MODAL_STATE_OPEN } = AppConstants;

const SiteSettingsModal = ({ onHide, autoOpenSection, siteSettings, user }) => {
  const designViewState = useStoreState(
    DesignViewStore,
    DesignViewStore.getState,
  );
  const canvasSets = useStoreState(
    SiteDesignStore,
    SiteDesignStore.getCanvasSetList,
  );
  const siteId = useSiteState().get("site_design_id");
  const { canToggle, settled: rendererSettled } = useRenderer(siteId);
  const isRendererLocked = rendererSettled && !canToggle;
  const isSaving = siteSettings?.get("saving");
  const isDirty = siteSettings?.get("dirty");
  const selectedTab = siteSettings?.get("tab");
  const selectedTabView = siteSettings?.get("view");
  const { globalCode, globalCodeData } = useStoreState(
    SiteDesignStore,
    SiteDesignStore.getGlobalCode,
  );

  const isOpen =
    designViewState.getIn(["modals", MODAL_SITE_SETTINGS, "state"]) ===
    MODAL_STATE_OPEN;

  useEffect(() => {
    if (autoOpenSection && isOpen) {
      SiteSettingsActions.changeTab(autoOpenSection);
    }
  }, [isOpen, autoOpenSection]);

  const _exportDesign = () => {
    const exportParams = {
      designType: AppConstants.DESIGN_TYPE_SITE,
    };

    SiteSettingsActions.closeDialog();
    ExportActions.exportDesignBegin(exportParams);
  };

  const _showChangesDialog = (proceedCallback) => {
    if (isDirty) {
      UIActions.modalOpen(AppConstants.MODAL_ACTION_CONFIRM, {
        title: "Save Site Settings",
        question: (
          <div>
            <div>You have unsaved changes</div>
            <div>Do you want to continue without saving?</div>
          </div>
        ),
        confirmBtn: "Yes",
        confirmAction: proceedCallback,
        cancelBtn: "No",
      });
    } else {
      proceedCallback();
    }
  };

  const _onHide = () => {
    _showChangesDialog(onHide);
  };

  const parsedGlobalCodeTabs = globalCode.map((codeID) => ({
    id: globalCodeData.getIn([codeID, "id"], ""),
    label: globalCodeData.getIn([codeID, "label"], ""),
  }));

  const _stageSiteSettingsChange = (propInfo) => {
    SiteSettingsActions.updateProperty(propInfo);
  };

  const _updateSiteDomain = (propInfo) => {
    const updatedPropInfo = {
      ...propInfo,
      action: AppConstants.UPDATE_ACTION_SET,
    };
    _stageSiteSettingsChange(updatedPropInfo);
  };

  const saveSiteSettings = () => {
    const allowSaving = !siteSettings.get("saving", false);
    if (allowSaving) {
      SiteSettingsActions.saveSettings();
      UserActions.trackEvent({
        event: "site-save",
        meta: {
          siteId: siteSettings.getIn(["site", "site_design_id"]),
          siteName: siteSettings.getIn(["site", "name"]),
          domain: siteSettings.getIn(["site", "domain"]),
          subdomain: siteSettings.getIn(["site", "subdomain"]),
          googleAnalyticsMeasurementId: siteSettings.getIn([
            "config",
            "googleAnalyticsMeasurementId",
          ]),
          fbAppId: siteSettings.getIn(["config", "fbAppId"]),
        },
      });
    }
  };

  const _selectTab = (tab) => {
    _showChangesDialog(() => {
      SiteSettingsActions.changeTab(tab);
    });
  };

  const _selectChildTab = (viewID, parentTabID) => {
    _showChangesDialog(() => {
      SiteSettingsActions.changeView(viewID, parentTabID);
    });
  };

  const getTabContent = () =>
    selectedTab === TAB_BLOG ? (
      <BlogSettings
        siteSettings={siteSettings}
        onChange={_updateSiteDomain}
        user={user}
      />
    ) : selectedTab === TAB_SOCIAL ? (
      <SocialLinks
        siteSettings={siteSettings}
        user={user}
        onChange={_stageSiteSettingsChange}
      />
    ) : selectedTab === TAB_INTEGRATIONS ? (
      <Integrations
        siteSettings={siteSettings}
        onChange={_stageSiteSettingsChange}
        selectedTab={selectedTab}
        globalCode={globalCode}
        globalCodeData={globalCodeData}
        selectedChildTab={selectedTabView}
      />
    ) : selectedTab === TAB_UPGRADE ? (
      <UpgradePanel />
    ) : (
      <SiteInfo
        showChangesDialog={_showChangesDialog}
        siteSettings={siteSettings}
        user={user}
        onChange={_stageSiteSettingsChange}
      />
    );

  const _cancelChanges = () => {
    SiteSettingsActions.changeView(VIEW_TABS);
  };

  const tabActions = {
    integrations: {
      label: (
        <Button
          variant="outline"
          iconPrefix="add"
          size="sm"
          className={styles.customCodeBtn}
          lowercase
        >
          Add Custom Code
        </Button>
      ),
      onClick: () => {
        const newCustomCode = generateCustomCode(
          globalCode.size,
          canvasSets?.map((canvasSet) => canvasSet.get("id")),
        );
        DocumentStore.dispatch({ type: COMMANDS.ADD_GLOBAL_CODE, payload: newCustomCode });
        _selectChildTab(newCustomCode.id, selectedTab);
      },
    },
  };

  const _getProperties = () => {
    switch (selectedTab) {
      case "site":
        return <SiteInformationProperties siteSettings={siteSettings} />;
      default:
        return null;
    }
  };
  const showCancelButton = isDirty && selectedTabView !== VIEW_TABS;

  const hasFacebookAppId = siteSettings.getIn(["config", "fbAppId"]);
  const isIntegrationsTab = selectedTab === TAB_INTEGRATIONS;
  const hasCustomCode = !!globalCode.size;
  const standardTabs = TAB_VIEW_LABEL_MAP[selectedTab] || [];

  // only showing the fb app id tab if the user has a fb app id
  const filteredStandardTabs = standardTabs.filter(
    (tab) => tab.id !== VIEW_FACEBOOK_APP_ID || hasFacebookAppId,
  );

  // if the integrations tab is selected and there is custom code, show the custom code tabs
  const customCodeTabs =
    isIntegrationsTab && hasCustomCode ? parsedGlobalCodeTabs?.toJS() : [];
  const tabChildrenWithCustomCode = [
    ...filteredStandardTabs,
    ...customCodeTabs,
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={_onHide}
      title="Site Settings"
      footer={
        <div className={styles.footerBar}>
          <div className={styles.footerLeft}>
            <Button variant="minimal" size="lg" onSIClick={_exportDesign}>
              Export Design
            </Button>
            <HelpTooltip tooltipKey="export-design" placement="top-center" />
          </div>
          <div className={styles.footerRight}>
            {showCancelButton && (
              <Button variant="minimal" size="lg" onSIClick={_cancelChanges}>
                Cancel
              </Button>
            )}
            <Button
              variant="feature"
              size="lg"
              data-testid="site-settings-save-button"
              onSIClick={saveSiteSettings}
            >
              {isSaving ? "Saving..." : isDirty ? "Save" : "Close"}
            </Button>
          </div>
        </div>
      }
    >
      <ContentPanel
        modalName={MODAL_SITE_SETTINGS}
        tabActions={tabActions}
        tabs={
          isRendererLocked
            ? TAB_LABEL_MAP.filter((tab) => tab.id !== TAB_UPGRADE)
            : TAB_LABEL_MAP
        }
        tabChildren={tabChildrenWithCustomCode}
        mainContent={getTabContent}
        propertiesPanel={_getProperties}
        onSelectTab={_selectTab}
        onSelectChildTab={_selectChildTab}
        selectedTab={selectedTab}
        selectedChildTab={selectedTabView}
      />
    </Modal>
  );
};

const ConnectedSiteSettingsModal = connectToStores(SiteSettingsModal, [
  {
    store: SiteSettingsStore,
    state: { siteSettings: null },
  },
  {
    store: SiteDesignStore,
    state: { globalCodeState: SiteDesignStore.getGlobalCode },
  },
]);

export default withAccountState(ConnectedSiteSettingsModal);
